import { motion } from "framer-motion";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useState } from "react";
import TextArea from "../../components/create-edit-form/TextArea";
import BackButtonHeaderPopUp from "../common/BackButtonHeader";

interface AddAIPromptPanelProps {
  currentPrompts: {
    text: string;
    primary: boolean;
  }[];
  limitPrompts: number;
  setPrompts: React.Dispatch<
    React.SetStateAction<
      {
        text: string;
        primary: boolean;
      }[]
    >
  >;
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Definition of the variants of this component
 */
const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const AddAIPromptPanel: React.FC<AddAIPromptPanelProps> = ({
  currentPrompts,
  limitPrompts,
  setPrompts,
  setShowPopUp,
}) => {
  // States definition
  const [prompt, setPrompt] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  // Define the criterion to activate the add buttons
  const activateSave = !!prompt;
  // Define the number of defined prompts
  const numPrompts = currentPrompts ? currentPrompts.length : 0;

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // Close pop up
    setShowPopUp(false);
  };

  /**
   * Function that adds the current prompt to the prompts list
   */
  const addPromptToList = () => {
    // Add the current prompt to the list
    setPrompts((currentList) => {
      // Take the current list as reference
      let listToReturn = currentList;

      // Check if the current prompt will be primary
      if (isPrimary) {
        // Make sure the rest of the data won't be primary
        listToReturn = listToReturn.map((prompt) => {
          return { ...prompt, primary: false };
        });
      }

      return [...listToReturn, { text: prompt, primary: isPrimary }];
    });
  };

  /**
   * Function executed when clicking "Add Prompt"
   */
  const handleAddPrompt = () => {
    // Add the prompt
    addPromptToList();

    // And close the pop up
    setShowPopUp(false);
  };

  /**
   * Function executed when clicking "Add and create another"
   */
  const handleAddAndCreateAnother = () => {
    // Add the prompt
    addPromptToList();

    // And reset the states
    setPrompt("");
    setIsPrimary(false);
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={() => setShowPopUp(false)}
      />
      <motion.div
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        className="fixed w-[462px] h-fit max-h-[90%] left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
      >
        <div className="relative w-full h-fit max-h-[70vh] flex-col justify-start items-start gap-6 inline-flex">
          <div className="justify-start items-center gap-3 flex">
            <BackButtonHeaderPopUp onClick={handlePressBack} />
            <div className="self-stretch inline-flex flex-col justify-center items-start gap-1">
              <div className="self-stretch justify-start text-neutral-600 text-lg font-normal font-sans leading-snug">
                Add an AI Prompt
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="justify-start text-neutral-500 text-xs font-normal font-sans leading-none">
                  {numPrompts} prompts added
                </div>
                <div className="w-px self-stretch bg-neutral-200" />
                <div className="justify-start text-neutral-500 text-xs font-normal font-sans leading-none">
                  {limitPrompts - numPrompts} prompts available
                </div>
              </div>
            </div>
          </div>
          <div className="w-full inline-flex flex-col gap-4 overflow-y-auto pop-up-scrollbar">
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
              <TextArea
                title="Prompt text*"
                placeholder="Provide the prompt text here"
                state={prompt}
                setState={setPrompt}
              />
              <div
                className="w-fit h-fit inline-flex justify-start items-center gap-2 cursor-pointer"
                onClick={() => setIsPrimary(!isPrimary)}
              >
                {isPrimary ? (
                  <MdCheckBox className="w-6 h-6 fill-neutral-700" />
                ) : (
                  <MdCheckBoxOutlineBlank className="w-6 h-6 fill-neutral-700" />
                )}
                <div className="flex-1 justify-center text-neutral-700 text-sm font-semibold font-sans leading-none select-none">
                  Make this my primary prompt
                </div>
              </div>
            </div>
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-3">
              <div className="self-stretch px-5 py-3 rounded inline-flex justify-center items-center gap-2">
                <div
                  className={`justify-start ${
                    activateSave
                      ? "text-secondary-600 hover:text-secondary-700 hover:underline cursor-pointer"
                      : "text-Subtle cursor-not-allowed"
                  } text-lg font-semibold font-sans leading-snug select-none`}
                  onClick={handleAddPrompt}
                >
                  Add Prompt
                </div>
              </div>
              <div
                className={`self-stretch px-5 py-3 ${
                  activateSave
                    ? "bg-secondary-600 hover:bg-secondary-700 text-neutral-50 cursor-pointer ease-in-out duration-150"
                    : "bg-Subtle text-neutral-400 cursor-not-allowed"
                } rounded inline-flex justify-center items-center gap-2 `}
                onClick={handleAddAndCreateAnother}
              >
                <div className="justify-start text-lg font-semibold font-sans leading-snug select-none">
                  Add and create another
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default AddAIPromptPanel;
